<script lang="ts" module>
  import type { GeneratorAttachedFile } from '@/teoCore/text/generator/GeneratorAttachedFile';
  import type { AddAttachedFileResult } from '@/teoCore/text/generator/GeneratorUiHandler';
  import { Icon } from '@/ui/base';
  import SmallGhostButton from '@/ui/elements/buttons/SmallGhostButton.svelte';
</script>

<script lang="ts">
  interface Props {
    files: GeneratorAttachedFile[];
    onAdd: (file: File) => Promise<AddAttachedFileResult>;
    onRemove: (fileId: string) => void;
    disabled?: boolean;
  }

  let { files, onAdd, onRemove, disabled = false }: Props = $props();

  let fileInput: HTMLInputElement | undefined = $state();
  let isDragging = $state(false);
  let errorMessage = $state<string | null>(null);
  let encoding = $state(false);

  const accept = '.pdf,.txt,image/jpeg,image/png,image/gif,image/webp,application/pdf,text/plain';

  async function handleFiles(fileList: FileList | File[]) {
    errorMessage = null;
    const list = Array.from(fileList);
    if (list.length === 0) return;

    encoding = true;
    try {
      for (const file of list) {
        const result = await onAdd(file);
        if (!result.ok) {
          errorMessage = result.message;
          break;
        }
      }
    } finally {
      encoding = false;
    }
  }

  function openPicker() {
    if (disabled || encoding) return;
    fileInput?.click();
  }

  async function onInputChange(event: Event) {
    const input = event.currentTarget as HTMLInputElement;
    if (input.files) {
      await handleFiles(input.files);
      input.value = '';
    }
  }

  function onDragOver(event: DragEvent) {
    event.preventDefault();
    if (!disabled && !encoding) isDragging = true;
  }

  function onDragLeave() {
    isDragging = false;
  }

  async function onDrop(event: DragEvent) {
    event.preventDefault();
    isDragging = false;
    if (disabled || encoding || !event.dataTransfer?.files) return;
    await handleFiles(event.dataTransfer.files);
  }
</script>

<div class="space-y-2">
  <div
    role="group"
    aria-label="Dateien anhängen"
    class="min-w-0 rounded-md border border-dashed p-2 transition-colors {isDragging ?
      'border-c-link bg-c-bg'
    : 'border-c-divider'}"
    ondragover={onDragOver}
    ondragleave={onDragLeave}
    ondrop={onDrop}
  >
    {#if files.length > 0}
      <ul class="mb-2 min-w-0 space-y-1">
        {#each files as file (file.id)}
          <li class="bg-c-bg flex min-w-0 items-start justify-between gap-2 rounded-sm px-2 py-1">
            <div class="flex min-w-0 flex-1 items-start gap-1">
              <Icon
                icon="NoteAdd"
                size={4}
                class="mt-0.5 shrink-0 text-c-light"
                aria-hidden="true"
              />
              <span class="min-w-0 flex-1 break-all text-sm" title={file.name}>{file.name}</span>
            </div>
            <span class="shrink-0">
              <SmallGhostButton
                icon="Close"
                label="Datei entfernen"
                type="button"
                disabled={disabled || encoding}
                onclick={() => onRemove(file.id)}
              />
            </span>
          </li>
        {/each}
      </ul>
    {/if}

    <button
      type="button"
      class="text-sm text-c-link disabled:text-c-disabled hocus:underline"
      disabled={disabled || encoding}
      onclick={openPicker}
    >
      {encoding ? 'Datei wird verarbeitet…' : '+ Datei hinzufügen'}
    </button>

    <input
      bind:this={fileInput}
      type="file"
      class="hidden"
      multiple
      {accept}
      {disabled}
      onchange={onInputChange}
    />
  </div>

  <p class="text-right text-xs text-c-disabled">Formate: PDF, TXT und Bilddateien</p>
  {#if errorMessage}
    <p class="text-sm text-c-error" role="alert">{errorMessage}</p>
  {/if}
</div>
